import { useEffect, useState } from "react";
import { MessageCircle, Menu, X } from "lucide-react";
import { waMessage, DEFAULT_WA_MSG, DATA_SHEETS_EMAIL_LINK } from "@/data";

const links = [
  { label: "Products", href: "#products", id: "products" },
  { label: "About", href: "#about", id: "about" },
  { label: "Data Sheets", href: DATA_SHEETS_EMAIL_LINK, id: "data-sheets", external: true },
  { label: "Contact", href: "#contact", id: "contact" },
];

export const scrollToSection = (e, href) => {
  if (e) e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -88 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    setOpen(false);
    scrollToSection(e, href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open
          ? "bg-obsidian/85 backdrop-blur-xl border-b border-gold/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[84px] flex items-center justify-between">
        <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-logo-link" className="flex items-center gap-3">
          <img
            src="/assets/logo.png"
            alt="Auto City Lubricants"
            className="h-14 w-auto object-contain bg-white rounded-lg px-2.5 py-1.5"
            data-testid="nav-logo"
          />
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-300 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </a>
            ) : (
              <a
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-300 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </a>
            )
          )}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-button"
            href={waMessage(DEFAULT_WA_MSG)}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-obsidian text-xs font-extrabold uppercase tracking-widest px-5 py-3 rounded-full hover:bg-gold-bright hover:shadow-[0_0_28px_rgba(229,169,60,0.45)] transition-all duration-300"
          >
            <MessageCircle size={15} strokeWidth={2.5} /> WhatsApp Us
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden text-gold p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div
          className="md:hidden bg-obsidian/95 backdrop-blur-xl border-t border-gold/10 px-6 py-6 flex flex-col gap-5"
          data-testid="nav-mobile-menu"
        >
          {links.map((l) =>
            l.external ? (
              <a
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-bold uppercase tracking-[0.25em] text-zinc-200"
              >
                {l.label}
              </a>
            ) : (
              <a
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                className="text-sm font-bold uppercase tracking-[0.25em] text-zinc-200"
              >
                {l.label}
              </a>
            )
          )}
          <a
            data-testid="nav-mobile-whatsapp"
            href={waMessage(DEFAULT_WA_MSG)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-gold text-obsidian text-xs font-extrabold uppercase tracking-widest px-5 py-3 rounded-full w-fit"
          >
            <MessageCircle size={15} strokeWidth={2.5} /> WhatsApp Us
          </a>
        </div>
      )}
    </header>
  );
}
